import { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useQuoteStore } from '../store'
import { useScopeHours } from '../../scope/useScopeHours'
import { useScopeStore } from '../../scope/store'
import { mapDbScopeToStoreState } from '../../scope/utils/mappers'
import {
  PLAN_OPTIONS,
  CURRENCY_OPTIONS,
  CONTRACT_TERM_OPTIONS,
  COUNTRY_OPTIONS,
  PORTAL_SEATS_OPTIONS,
  SUPPORT_LEVEL_OPTIONS,
  IMPLEMENTATION_SUPPORT_OPTIONS,
  HOSTED_ONESDK_OPTIONS,
  PAYMENT_FREQUENCY_OPTIONS,
} from '../data'
import type { PlanType, CurrencyType } from '../types'
import { Link2, Clock, CheckCircle, ChevronDown, ChevronUp, HelpCircle, AlertTriangle, GitBranch, Search } from 'lucide-react'
import { PLAN_PRICING } from '../data'
import { teamMembersApi, quotesApi, scopesApi, getAuthHeaders } from '@api/client'
import type { TeamMember } from '@api/client'
import { useAuthStore } from '@store/authStore'

interface AvailableScope {
  id: number
  deal_id: number
  total_hours: number
  total_cost: number
  scoped_by_name: string | null
  updated_at: string
  customer_name: string
  hubspot_deal_id: string | null
  hubspot_deal_name: string | null
  configured_sections: number
  related: number
}

function useTeamMemberOptions() {
  const [members, setMembers] = useState<TeamMember[]>([])

  useEffect(() => {
    teamMembersApi.list().then(res => {
      if (res.success && res.data) {
        setMembers(res.data.items.filter(m => m.is_active))
      }
    }).catch(() => {})
  }, [])

  return useMemo(() => [
    { value: '', label: 'Select...' },
    ...members.map(m => ({ value: m.name, label: m.name })),
  ], [members])
}

// Compact input component
function FormInput({
  label,
  type = 'text',
  value,
  onChange,
  placeholder,
  min,
  max,
  disabled,
}: {
  label: string
  type?: string
  value: string | number
  onChange: (value: string) => void
  placeholder?: string
  min?: number
  max?: number
  disabled?: boolean
}) {
  return (
    <div>
      <label className="block text-[10px] text-[var(--text-muted)] mb-1">{label}</label>
      <input
        type={type}
        value={type === 'number' && value === 0 ? '' : value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        min={min}
        max={max}
        disabled={disabled}
        className="w-full px-2.5 py-1.5 text-[11px] bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded text-[var(--text-primary)] placeholder-gray-600 focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary disabled:opacity-50"
      />
    </div>
  )
}

// Compact select component
function FormSelect({
  label,
  value,
  onChange,
  options,
}: {
  label: string
  value: string
  onChange: (value: string) => void
  options: { value: string; label: string }[]
}) {
  return (
    <div>
      <label className="block text-[10px] text-[var(--text-muted)] mb-1">{label}</label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-2.5 py-1.5 text-[11px] bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded text-[var(--text-primary)] focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
      >
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    </div>
  )
}

export function QuoteDetails() {
  const teamMemberOptions = useTeamMemberOptions()
  const [searchParams] = useSearchParams()
  const sessionId = searchParams.get('session')
  const {
    details,
    setCustomerName,
    setCountry,
    setPreparedBy,
    setStartDate,
    setCustomerPlan,
    setPlatformFees,
    setImplementationFee,
    applyScopeImplementationFee,
    setChildAccounts,
    setContractTermMonths,
    setPaymentFrequency,
    setAutoRenew,
    setPartnerCommission,
    setPortalSeats,
    setSupportLevel,
    setImplementationSupport,
    setHostedOneSDK,
    setMinimumCommitment,
    setDisplayCurrency,
    setScopeSourceDealId,
    quoteLocks,
  } = useQuoteStore()

  // Available scopes for scope picker
  const [availableScopes, setAvailableScopes] = useState<AvailableScope[]>([])
  const [scopePickerOpen, setScopePickerOpen] = useState(false)
  const [scopeSearch, setScopeSearch] = useState('')
  const scopeSearchTimerRef = useRef<ReturnType<typeof setTimeout>>()

  // Fetch available scopes when the session is loaded or search changes
  const fetchScopes = useCallback((search: string = '') => {
    if (!sessionId) return
    quotesApi.availableScopes(Number(sessionId), search || undefined).then((res) => {
      if (res.success && res.data) {
        setAvailableScopes(res.data.scopes)
      }
    }).catch(() => {})
  }, [sessionId])

  useEffect(() => { fetchScopes() }, [fetchScopes])

  // Debounced search
  useEffect(() => {
    if (!scopePickerOpen) return
    clearTimeout(scopeSearchTimerRef.current)
    scopeSearchTimerRef.current = setTimeout(() => fetchScopes(scopeSearch), 300)
    return () => clearTimeout(scopeSearchTimerRef.current)
  }, [scopeSearch, scopePickerOpen, fetchScopes])

  const { user } = useAuthStore()

  const handleScopeChange = useCallback(async (scopeDealId: number) => {
    const previousScope = availableScopes.find((s) => s.deal_id === (details.scopeSourceDealId || Number(sessionId)))
    const newScope = availableScopes.find((s) => s.deal_id === scopeDealId)

    setScopeSourceDealId(scopeDealId === Number(sessionId) ? null : scopeDealId)
    setScopePickerOpen(false)
    setScopeSearch('')
    try {
      const scopeRes = await scopesApi.getByDealId(scopeDealId)
      if (scopeRes.success && scopeRes.data) {
        const mapped = mapDbScopeToStoreState(scopeRes.data)
        useScopeStore.getState().setScope(mapped)
      }
    } catch {
      // best-effort
    }

    // Notify Slack about the scope change (best-effort, don't block the UI)
    if (newScope && previousScope && newScope.deal_id !== previousScope.deal_id) {
      const baseUrl = window.location.origin
      fetch('/api/slack/scope-change', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({
          dealId: Number(sessionId),
          customerName: details.customerName,
          changedByName: user?.name || user?.email || 'Unknown',
          changedByEmail: user?.email,
          previousScopeDealId: previousScope.deal_id,
          previousScopeHours: previousScope.total_hours,
          previousScopeSections: previousScope.configured_sections,
          newScopeDealId: newScope.deal_id,
          newScopeHours: newScope.total_hours,
          newScopeSections: newScope.configured_sections,
          quoteUrl: `${baseUrl}/quote?session=${sessionId}`,
          scopeUrl: `${baseUrl}/scope?session=${scopeDealId}`,
        }),
      }).catch(() => {})
    }
  }, [sessionId, setScopeSourceDealId, availableScopes, details.scopeSourceDealId, details.customerName, user])

  // Get scope hours
  const { totalHours, autoHours, manualHours, featureRequestHours } = useScopeHours()
  const scopeImplementationFee = totalHours * details.hourlyRate

  // Source-of-truth for "is this fee from the scope?" is now the PERSISTED
  // flag on the quote (details.implementationFeeFromScope), set when the user
  // clicks "Use Scope Hours" and cleared on any manual edit — so it survives
  // save/reload and is reliable for reviewers, unlike the old equality check.
  const isFromScope = details.implementationFeeFromScope
  // Stale = it was sourced from the scope, but the scope (hours or rate) has
  // since moved, so the stored fee no longer matches what the scope produces.
  const isStale = isFromScope && Math.abs(details.implementationFee - scopeImplementationFee) > 0.5
  const isScopeSynced = isFromScope && !isStale

  // The deal ID whose scope is currently feeding this quote
  const activeScopeDealId = details.scopeSourceDealId || Number(sessionId)
  const activeScope = availableScopes.find((s) => s.deal_id === activeScopeDealId)
  // Expand the panel by default until the fee is cleanly synced from the scope.
  const [isScopeExpanded, setIsScopeExpanded] = useState(!isScopeSynced)

  const handleUseScopeHours = () => {
    applyScopeImplementationFee(totalHours, scopeImplementationFee)
    setIsScopeExpanded(false)
  }

  return (
    <div className="space-y-4">
    {/* fieldset disabled locks all form controls while quote is under review */}
    <fieldset disabled={quoteLocks.all} className="space-y-4 border-0 p-0 m-0 min-w-0">
      {/* Deal Details Section */}
      <div className="bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg p-4">
        <h3 className="text-sm font-medium text-[var(--text-primary)] mb-3 flex items-center gap-2">
          <span>💼</span> Deal Details
        </h3>

        {/* Customer Name — full width */}
        <div className="mb-3">
          <FormInput
            label="Customer Name"
            value={details.customerName}
            onChange={setCustomerName}
            placeholder="Enter customer name"
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <FormSelect
            label="Country"
            value={details.country}
            onChange={setCountry}
            options={COUNTRY_OPTIONS}
          />

          <FormSelect
            label="Prepared By"
            value={details.preparedBy}
            onChange={setPreparedBy}
            options={teamMemberOptions}
          />

          <FormInput
            label="Start Date"
            type="date"
            value={details.startDate}
            onChange={setStartDate}
          />

          <FormSelect
            label="Term"
            value={String(details.contractTermMonths)}
            onChange={(v) => setContractTermMonths(Number(v))}
            options={CONTRACT_TERM_OPTIONS}
          />

          <FormSelect
            label="Customer Plan"
            value={details.customerPlan}
            onChange={(v) => setCustomerPlan(v as PlanType)}
            options={PLAN_OPTIONS}
          />

          <FormSelect
            label="Auto-Renew"
            value={details.autoRenew ? 'Yes' : 'No'}
            onChange={(v) => setAutoRenew(v === 'Yes')}
            options={[
              { value: 'Yes', label: 'Yes' },
              { value: 'No', label: 'No' },
            ]}
          />

          <FormSelect
            label="Frequency"
            value={details.paymentFrequency}
            onChange={(v) => setPaymentFrequency(v as 'Annual' | 'Quarterly' | 'Monthly')}
            options={PAYMENT_FREQUENCY_OPTIONS}
          />

          <FormSelect
            label="Currency"
            value={details.displayCurrency}
            onChange={(v) => setDisplayCurrency(v as CurrencyType)}
            options={CURRENCY_OPTIONS}
          />
        </div>
      </div>
    </fieldset>

      {/* Scope Picker — outside fieldset so it works even when quote is locked */}
      {sessionId && (
        <div className="bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-[var(--text-primary)] flex items-center gap-2">
              <GitBranch size={14} />
              Linked Scope
            </h3>
            <button
              onClick={() => {
                const opening = !scopePickerOpen
                setScopePickerOpen(opening)
                if (!opening) setScopeSearch('')
              }}
              className="text-[10px] px-2 py-1 rounded bg-primary/10 border border-primary/30 text-primary hover:bg-primary/20 transition-colors font-medium"
            >
              Change
            </button>
          </div>

          {activeScope && (
            <div className="text-[11px] text-[var(--text-secondary)]">
              <span className="font-medium text-[var(--text-primary)]">
                Deal #{activeScope.deal_id}
              </span>
              {activeScope.hubspot_deal_name && (
                <span className="ml-1">— {activeScope.hubspot_deal_name}</span>
              )}
              <span className="ml-2 text-primary font-medium">{activeScope.total_hours} hrs</span>
              <span className="ml-1 text-[var(--text-muted)]">
                ({activeScope.configured_sections}/8 sections)
              </span>
            </div>
          )}

          {scopePickerOpen && (() => {
            const relatedScopes = availableScopes.filter((s) => s.related === 1)
            const otherScopes = availableScopes.filter((s) => s.related !== 1)

            const renderScope = (s: AvailableScope) => {
              const isActive = s.deal_id === activeScopeDealId
              const completionPct = Math.round((s.configured_sections / 8) * 100)
              return (
                <button
                  key={s.id}
                  onClick={() => handleScopeChange(s.deal_id)}
                  className={`w-full text-left px-3 py-2.5 rounded-lg border transition-colors text-[11px] ${
                    isActive
                      ? 'bg-primary/10 border-primary/40 text-primary'
                      : 'bg-[var(--bg-tertiary)] border-[var(--border-color)] hover:border-primary/30 text-[var(--text-primary)]'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {isActive && <CheckCircle size={12} className="text-primary shrink-0" />}
                      <span className="font-medium">Deal #{s.deal_id}</span>
                      {s.deal_id === Number(sessionId) && (
                        <span className="text-[9px] px-1 py-0.5 rounded bg-[var(--bg-tertiary)] text-[var(--text-muted)]">
                          this deal
                        </span>
                      )}
                      {s.related !== 1 && (
                        <span className="text-[9px] px-1 py-0.5 rounded bg-[var(--bg-tertiary)] text-[var(--text-muted)] truncate max-w-[100px]">
                          {s.customer_name}
                        </span>
                      )}
                    </div>
                    <span className="font-bold text-primary">{s.total_hours} hrs</span>
                  </div>
                  <div className="flex items-center justify-between mt-1 text-[var(--text-muted)]">
                    <div className="flex items-center gap-2">
                      {s.hubspot_deal_name && <span className="truncate max-w-[140px]">{s.hubspot_deal_name}</span>}
                      {s.scoped_by_name && <span>by {s.scoped_by_name}</span>}
                    </div>
                    <div className="flex items-center gap-1.5">
                      <div className="w-12 h-1 rounded-full bg-[var(--border-color)] overflow-hidden">
                        <div
                          className={`h-full rounded-full ${completionPct >= 50 ? 'bg-success' : completionPct > 0 ? 'bg-warning' : 'bg-[var(--text-muted)]'}`}
                          style={{ width: `${completionPct}%` }}
                        />
                      </div>
                      <span className="text-[9px]">{completionPct}%</span>
                    </div>
                  </div>
                </button>
              )
            }

            return (
              <div className="mt-3 space-y-1.5 border-t border-[var(--border-color)] pt-3">
                <p className="text-[10px] text-[var(--text-muted)] mb-2">
                  Select a scope to use for this quote's implementation hours:
                </p>
                <div className="relative mb-2">
                  <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
                  <input
                    type="text"
                    value={scopeSearch}
                    onChange={(e) => setScopeSearch(e.target.value)}
                    placeholder="Search by deal ID or customer name..."
                    className="w-full pl-7 pr-3 py-1.5 text-[11px] rounded-md border border-[var(--border-color)] bg-[var(--bg-primary)] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:outline-none focus:border-primary/50"
                    autoFocus
                  />
                </div>
                {relatedScopes.length > 0 && (
                  <>
                    {otherScopes.length > 0 && (
                      <p className="text-[9px] font-medium text-[var(--text-muted)] uppercase tracking-wider mb-1">Related scopes</p>
                    )}
                    {relatedScopes.map(renderScope)}
                  </>
                )}
                {otherScopes.length > 0 && (
                  <>
                    <p className="text-[9px] font-medium text-[var(--text-muted)] uppercase tracking-wider mt-3 mb-1">Other scopes with data</p>
                    {otherScopes.map(renderScope)}
                  </>
                )}
                {availableScopes.length === 0 && (
                  <p className="text-[10px] text-[var(--text-muted)] italic">No scopes found.</p>
                )}
              </div>
            )
          })()}
        </div>
      )}

    <fieldset disabled={quoteLocks.all} className="space-y-4 border-0 p-0 m-0 min-w-0">
      {/* Scope Hours Integration */}
      {totalHours > 0 && (
        <div className={`bg-[var(--bg-secondary)] border ${isScopeSynced ? 'border-success/30' : isStale ? 'border-warning/40' : 'border-primary/30'} rounded-lg p-4`}>
          <button
            onClick={() => setIsScopeExpanded(!isScopeExpanded)}
            className="w-full flex items-center justify-between text-sm font-medium text-[var(--text-primary)]"
          >
            <div className="flex items-center gap-2">
              {isScopeSynced ? (
                <CheckCircle size={16} className="text-success" />
              ) : isStale ? (
                <AlertTriangle size={16} className="text-warning" />
              ) : (
                <Clock size={16} className="text-primary" />
              )}
              <span>Scope Hours</span>
              {isScopeSynced && (
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-success/20 text-success font-medium">
                  Applied
                </span>
              )}
              {isStale && (
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-warning/20 text-warning font-medium">
                  Out of sync
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <span className="text-primary font-bold text-[11px]">{totalHours.toFixed(1)} hrs</span>
              {isScopeExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            </div>
          </button>

          {isScopeExpanded && (
            <>
              {totalHours === 0 && (
                <div className="mt-3 mb-2 text-[11px] text-[var(--text-muted)]">
                  This scope has no hours configured. Use the scope picker above to link a scope with hours.
                </div>
              )}
              {totalHours > 0 && (
              <div className="space-y-2 text-[11px] mt-3 mb-3">
                <div className="flex justify-between">
                  <span className="text-[var(--text-muted)]">Auto-generated</span>
                  <span className="text-[var(--text-primary)]">{autoHours.toFixed(1)} hrs</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[var(--text-muted)]">Manual entries</span>
                  <span className="text-[var(--text-primary)]">{manualHours.toFixed(1)} hrs</span>
                </div>
                {featureRequestHours > 0 && (
                  <div className="flex justify-between">
                    <span className="text-[var(--text-muted)]">Feature requests</span>
                    <span className="text-[var(--text-primary)]">{featureRequestHours.toFixed(1)} hrs</span>
                  </div>
                )}
                <div className="flex justify-between pt-2 border-t border-[var(--border-color)]">
                  <span className="text-[var(--text-primary)] font-medium">Total Hours</span>
                  <span className="text-primary font-bold">{totalHours.toFixed(1)} hrs</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[var(--text-muted)]">@ ${details.hourlyRate}/hr</span>
                  <span className="text-primary font-medium">
                    ${scopeImplementationFee.toLocaleString()}
                  </span>
                </div>
              </div>
              )}

              {isStale && details.implementationScopeHours !== null && (
                <div className="flex items-start gap-2 mb-2 px-2.5 py-2 rounded bg-warning/10 border border-warning/30 text-[10px] text-warning">
                  <AlertTriangle size={12} className="mt-0.5 shrink-0" />
                  <span>
                    Scope changed since this fee was applied — was{' '}
                    <strong>{details.implementationScopeHours.toFixed(1)} hrs</strong>, now{' '}
                    <strong>{totalHours.toFixed(1)} hrs</strong>. Re-sync to update the implementation fee.
                  </span>
                </div>
              )}

              {isScopeSynced && details.implementationScopeAppliedAt && (
                <div className="flex items-center gap-1.5 text-[10px] text-success">
                  <CheckCircle size={12} className="shrink-0" />
                  <span>
                    Sourced from scope · applied{' '}
                    {new Date(details.implementationScopeAppliedAt).toLocaleDateString()}
                  </span>
                </div>
              )}

              {!isScopeSynced && totalHours > 0 && (
                <button
                  onClick={handleUseScopeHours}
                  className={`w-full flex items-center justify-center gap-2 px-3 py-2 text-[11px] font-medium rounded-lg transition-colors ${isStale ? 'bg-warning/10 border border-warning/40 text-warning hover:bg-warning/20' : 'bg-primary/10 border border-primary/30 text-primary hover:bg-primary/20'}`}
                >
                  <Link2 size={14} />
                  {isStale ? 'Re-sync with current scope' : 'Use Scope Hours for Implementation Fee'}
                </button>
              )}
            </>
          )}
        </div>
      )}

      {/* License & Fees Section */}
      <div className="bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg p-4">
        <h3 className="text-sm font-medium text-[var(--text-primary)] mb-3 flex items-center gap-2">
          <span>🏷️</span> License & Fees
        </h3>

        <div className="grid grid-cols-2 gap-3 items-end">
          <FormInput
            label="Platform Fee (Annual)"
            type="number"
            value={details.platformFees}
            onChange={(v) => setPlatformFees(Number(v))}
          />

          <div>
            <label className="block text-[10px] text-[var(--text-muted)] mb-1 whitespace-nowrap">
              Implementation Fee
              {totalHours > 0 && !isScopeSynced && scopeImplementationFee !== details.implementationFee && (
                <span className={isStale ? 'text-warning ml-1' : 'text-primary ml-1'}>(Scope: ${scopeImplementationFee.toLocaleString()})</span>
              )}
            </label>
            <input
              type="number"
              value={details.implementationFee || ''}
              // Locked alone in the 'reviewed' state so the submitter can't clobber the
              // Impl team's injected fee before continuing. quoteLocks.all already covers
              // the in_review / awaiting states via the surrounding fieldset.
              disabled={quoteLocks.all || quoteLocks.implementationFee}
              // Manual edits go through setImplementationFee, which clears the
              // persisted "from scope" provenance in the store.
              onChange={(e) => setImplementationFee(Number(e.target.value))}
              className={`w-full px-2.5 py-1.5 text-[11px] bg-[var(--bg-tertiary)] border ${isScopeSynced ? 'border-success/50' : isStale ? 'border-warning/50' : 'border-[var(--border-color)]'} rounded text-[var(--text-primary)] placeholder-gray-600 focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary`}
            />
          </div>

          <div>
            <label className="flex items-center gap-1 text-[10px] text-[var(--text-muted)] mb-1 whitespace-nowrap">
              Min. Commitment (Ann.)
              {details.customerPlan && (
                <span className="relative group">
                  <HelpCircle size={11} className="text-[var(--text-muted)] cursor-help shrink-0" />
                  <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 hidden group-hover:block w-48 px-2.5 py-2 text-[10px] bg-[var(--bg-primary)] border border-[var(--border-color)] rounded shadow-lg text-[var(--text-primary)] z-50 leading-relaxed whitespace-normal">
                    <span className="font-medium">{details.customerPlan} plan default:</span>
                    <br />
                    ${(PLAN_PRICING[details.customerPlan].minimumCommitment / 12).toLocaleString()}/mo × 12 = ${PLAN_PRICING[details.customerPlan].minimumCommitment.toLocaleString()}/yr
                  </span>
                </span>
              )}
            </label>
            <input
              type="number"
              value={details.minimumCommitment || ''}
              onChange={(e) => setMinimumCommitment(Number(e.target.value))}
              min={0}
              className="w-full px-2.5 py-1.5 text-[11px] bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded text-[var(--text-primary)] placeholder-gray-600 focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
            />
          </div>

          <FormInput
            label="Child Accounts"
            type="number"
            value={details.childAccounts}
            onChange={(v) => setChildAccounts(Number(v))}
            min={0}
          />

          <FormSelect
            label="Portal Seats"
            value={String(details.portalSeats)}
            onChange={(v) => setPortalSeats(Number(v))}
            options={PORTAL_SEATS_OPTIONS.map((o) => ({
              value: o.value,
              label: `${o.value} seats - $${Number(o.value) === 0 ? '0' : Number(o.value) === 5 ? '1,500' : Number(o.value) === 10 ? '3,000' : '9,000'}`,
            }))}
          />

          <FormSelect
            label="Support Level"
            value={String(details.supportLevel)}
            onChange={(v) => setSupportLevel(Number(v))}
            options={SUPPORT_LEVEL_OPTIONS.map((o) => ({
              value: o.value,
              label: o.label.replace('$', '').replace('/yr', ''),
            }))}
          />

          <FormSelect
            label="Implementation Support"
            value={String(details.implementationSupport)}
            onChange={(v) => setImplementationSupport(Number(v))}
            options={IMPLEMENTATION_SUPPORT_OPTIONS.map((o) => ({
              value: o.value,
              label: o.value === '0' ? 'None' : o.value === '24375' ? 'Standard (130 hrs) - $24,375' : 'Extended - $48,750',
            }))}
          />

          <FormSelect
            label="Hosted OneSDK"
            value={String(details.hostedOneSDK)}
            onChange={(v) => setHostedOneSDK(Number(v))}
            options={HOSTED_ONESDK_OPTIONS.map((o) => ({
              value: o.value,
              label: o.value === '0' ? 'No - $0' : 'Yes - $6,000',
            }))}
          />

          <FormInput
            label="Partner Commission (%)"
            type="number"
            value={details.partnerCommission}
            onChange={(v) => setPartnerCommission(Number(v))}
            min={0}
            max={100}
          />
        </div>
      </div>
    </fieldset>
    </div>
  )
}
